import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Play, Sparkles, TrendingUp, Newspaper, Award, Download, Quote } from "lucide-react";
import api from "@/lib/api";
import { useDataSaver } from "@/context/DataSaverContext";
import Particles from "@/components/Particles";
import Countdown from "@/components/Countdown";
import CandidateCard from "@/components/CandidateCard";
import AfricaMapInteractive from "@/components/AfricaMapInteractive";
import { Reveal, Stagger, StaggerItem } from "@/components/Reveal";

const HERO = "https://images.unsplash.com/photo-1733322987267-f691d5be2bc6?crop=entropy&cs=srgb&fm=jpg&w=1920&q=80";

function SectionHead({ kicker, title, cta, to }) {
  return (
    <div className="mb-8 flex items-end justify-between gap-4">
      <div>
        <p className="kicker mb-2">{kicker}</p>
        <h2 className="text-3xl sm:text-4xl lg:text-5xl text-amber-50">{title}</h2>
      </div>
      {cta && (
        <Link to={to} className="hidden sm:inline-flex items-center gap-1.5 text-sm font-display uppercase tracking-wider text-gold hover:gap-2.5 transition-all">
          {cta} <ArrowRight className="h-4 w-4" />
        </Link>
      )}
    </div>
  );
}

export default function Home() {
  const { dataSaver } = useDataSaver();
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const heroBgY = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const heroBgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.18]);
  const heroContentY = useTransform(scrollYProgress, [0, 1], ["0%", "40%"]);
  const heroContentOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);
  const [stats, setStats] = useState(null);
  const [featured, setFeatured] = useState([]);
  const [trending, setTrending] = useState([]);
  const [board, setBoard] = useState([]);
  const [delegations, setDelegations] = useState([]);
  const [news, setNews] = useState([]);
  const [sponsors, setSponsors] = useState([]);
  const [videos, setVideos] = useState([]);
  const [deferredPrompt, setDeferredPrompt] = useState(null);

  useEffect(() => {
    const handler = (e) => { e.preventDefault(); setDeferredPrompt(e); };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const installApp = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === "accepted") toast.success("Installing… welcome to the movement!");
      setDeferredPrompt(null);
    } else if (window.matchMedia("(display-mode: standalone)").matches) {
      toast.success("You're already using the installed app!");
    } else {
      toast("Add to Home Screen", { description: "Open your browser menu and choose \"Add to Home screen\" / \"Install app\"." });
    }
  };

  useEffect(() => {
    api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/candidates", { params: { featured: true, limit: 8 } }).then((r) => setFeatured(r.data)).catch(() => {});
    api.get("/candidates", { params: { sort: "trending", limit: 4 } }).then((r) => setTrending(r.data)).catch(() => {});
    api.get("/leaderboard", { params: { limit: 5 } }).then((r) => setBoard(r.data)).catch(() => {});
    api.get("/delegations").then((r) => setDelegations(r.data)).catch(() => {});
    api.get("/news", { params: { limit: 4 } }).then((r) => setNews(r.data)).catch(() => {});
    api.get("/sponsors").then((r) => setSponsors(r.data)).catch(() => {});
    api.get("/videos").then((r) => setVideos(r.data)).catch(() => {});
  }, []);

  return (
    <div>
      {/* HERO */}
      <section ref={heroRef} className="relative grain overflow-hidden" data-testid="hero-section">
        <motion.div className="absolute inset-0" style={dataSaver ? undefined : { y: heroBgY, scale: heroBgScale }}>
          <img src={HERO} alt="Miss United States of Africa continental stage" className="h-full w-full object-cover" loading="eager" />
          <div className="absolute inset-0 bg-gradient-to-b from-obsidian/70 via-obsidian/80 to-obsidian" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(230,0,92,0.15),transparent_60%)]" />
        </motion.div>
        <Particles count={30} />
        <motion.div className="section relative z-10 flex min-h-[88vh] flex-col justify-center py-20"
          style={dataSaver ? undefined : { y: heroContentY, opacity: heroContentOpacity }}>
          <Reveal delay={0.05}>
            <p className="kicker mb-4 flex items-center gap-2" data-testid="hero-kicker">
              <Sparkles className="h-4 w-4 text-gold" /> The Continental Pageant · Edition {stats?.edition_year || 2026}
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <h1 className="max-w-4xl text-5xl sm:text-6xl lg:text-8xl leading-[0.95] text-amber-50">
              MISS UNITED STATES <span className="gold-text">OF AFRICA</span>
            </h1>
          </Reveal>
          <Reveal delay={0.28}>
            <p className="mt-5 max-w-xl font-display text-lg text-amber-100/70">
              Beauty • Culture • Leadership • Unity • Impact.
              <span className="block gold-text text-xl mt-1">One Africa • One People • One Future.</span>
            </p>
          </Reveal>
          <Reveal delay={0.4}>
            <div className="mt-9 flex flex-wrap gap-3" data-testid="hero-ctas">
              <Link to="/candidates" className="btn-gold" data-testid="cta-candidates">Meet the Candidates</Link>
              <Link to="/vote" className="btn-ghost-gold" data-testid="cta-vote">Vote Now</Link>
              <Link to="/watch" className="btn-ghost-gold inline-flex items-center gap-2" data-testid="cta-watch">
                <Play className="h-4 w-4" /> Watch
              </Link>
            </div>
            <div className="mt-6 flex flex-wrap gap-3 text-xs">
              <Link to="/sponsors" className="text-amber-100/60 hover:text-gold underline underline-offset-4">Become a Sponsor</Link>
              <span className="text-amber-100/30">·</span>
              <Link to="/support" className="text-amber-100/60 hover:text-gold underline underline-offset-4">Support a Candidate</Link>
            </div>
          </Reveal>

          {stats?.finale_date && (
            <Reveal delay={0.52}>
              <div className="mt-12 glass inline-flex flex-col gap-3 rounded-xl p-5 sm:flex-row sm:items-center sm:gap-8 w-fit" data-testid="hero-countdown">
                <div>
                  <p className="kicker mb-1">Grand Coronation Finale</p>
                  <p className="font-serif text-lg text-amber-50">Live from Lagos, Nigeria</p>
                </div>
                <Countdown target={stats.finale_date} />
              </div>
            </Reveal>
          )}
        </motion.div>
      </section>

      {/* FEATURED CANDIDATES */}
      <section className="section py-16 sm:py-24">
        <Reveal><SectionHead kicker="The Delegates" title="Featured Candidates" cta="View all" to="/candidates" /></Reveal>
        <Stagger className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4" data-testid="featured-candidates">
          {featured.map((c, i) => <StaggerItem key={c.id}><CandidateCard candidate={c} index={i} /></StaggerItem>)}
        </Stagger>
      </section>

      {/* EXPLORE AFRICA */}
      <section className="relative border-y border-gold/15 bg-obsidian-800 py-16 sm:py-24">
        <div className="section">
          <Reveal><SectionHead kicker="Explore the Continent" title="The Africa Engagement Map" cta="All delegations" to="/delegations" /></Reveal>
          <Reveal delay={0.1}>
            <p className="mb-8 max-w-2xl text-amber-100/60">
              Tap any of our {stats?.total_delegations || 56} participating delegations to meet their candidate,
              explore their heritage and cast your support.
            </p>
          </Reveal>
          <motion.div
            initial={dataSaver ? false : { opacity: 0, scale: 0.9, rotateX: 12 }}
            whileInView={dataSaver ? undefined : { opacity: 1, scale: 1, rotateX: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 1.0, ease: [0.16, 1, 0.3, 1] }}
            style={{ transformPerspective: 1400 }}
          >
            <AfricaMapInteractive delegations={delegations} />
          </motion.div>
        </div>
      </section>

      {/* TRENDING + LEADERBOARD */}
      <section className="section grid gap-12 py-16 sm:py-24 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <SectionHead kicker="Rising Now" title="Trending Candidates" />
          <div className="stagger grid grid-cols-2 gap-4 sm:grid-cols-4">
            {trending.map((c, i) => <CandidateCard key={c.id} candidate={c} index={i} />)}
          </div>
        </div>
        <div>
          <div className="mb-8">
            <p className="kicker mb-2 flex items-center gap-2"><TrendingUp className="h-4 w-4 text-gold" /> Live Rankings</p>
            <h2 className="text-3xl text-amber-50">Leaderboard</h2>
          </div>
          <div className="card-lux divide-y divide-gold/10" data-testid="leaderboard">
            {board.map((c, i) => (
              <Link key={c.id} to={`/candidates/${c.slug}`} className="flex items-center gap-3 p-4 hover:bg-obsidian-600">
                <span className={`font-mono text-lg w-6 ${i === 0 ? "gold-text" : "text-muted-foreground"}`}>{c.rank}</span>
                <span className="text-2xl">{c.flag}</span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm text-amber-50">{c.name}</span>
                  <span className="block text-xs text-muted-foreground">{c.delegation_name}</span>
                </span>
                <span className="font-mono text-sm gold-text tabular-nums">{c.votes.toLocaleString()}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* VIDEOS */}
      <section className="border-y border-gold/15 bg-obsidian-800 py-16 sm:py-24">
        <div className="section">
          <Reveal><SectionHead kicker="Broadcast" title="Latest Videos" cta="Watch center" to="/watch" /></Reveal>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="latest-videos">
            {videos.map((v) => (
              <Link key={v.id} to="/watch" className="card-lux group">
                <div className="relative aspect-video overflow-hidden bg-obsidian-600">
                  {!dataSaver && <img src={v.thumbnail} alt={v.title} loading="lazy" className="h-full w-full object-cover opacity-70 transition-transform group-hover:scale-105" />}
                  <span className="absolute inset-0 flex items-center justify-center">
                    <span className="flex h-12 w-12 items-center justify-center rounded-full gold-bg text-obsidian"><Play className="h-5 w-5" /></span>
                  </span>
                  <span className="absolute bottom-2 right-2 rounded bg-black/70 px-1.5 py-0.5 font-mono text-[0.6rem] text-amber-200">{v.duration}</span>
                </div>
                <div className="p-3">
                  <p className="kicker mb-1 text-[0.6rem]">{v.type}</p>
                  <p className="text-sm text-amber-50 leading-snug">{v.title}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* NEWS */}
      <section className="section py-16 sm:py-24">
        <Reveal><SectionHead kicker="Newsroom" title="Latest News" cta="All news" to="/news" /></Reveal>
        <Stagger className="grid gap-4 md:grid-cols-2 lg:grid-cols-4" data-testid="latest-news">
          {news.map((n) => (
            <StaggerItem key={n.id}>
              <article className="card-lux h-full p-5">
                <p className="kicker mb-3 flex items-center gap-1.5"><Newspaper className="h-3 w-3" /> {n.category}</p>
                <h3 className="mb-2 font-serif text-lg text-amber-50 leading-tight">{n.title}</h3>
                <p className="text-sm text-amber-100/60 line-clamp-3">{n.excerpt}</p>
              </article>
            </StaggerItem>
          ))}
        </Stagger>
      </section>

      {/* QUOTE */}
      <section className="relative overflow-hidden border-y border-gold/15 bg-obsidian-800 py-20">
        <Reveal className="section text-center">
          <Quote className="mx-auto mb-5 h-8 w-8 text-gold/50" />
          <p className="mx-auto max-w-3xl font-serif text-2xl sm:text-4xl leading-snug text-amber-50">
            When one African woman rises, <span className="gold-text">the whole continent rises with her.</span>
          </p>
        </Reveal>
      </section>

      {/* SPONSORS */}
      <section className="section py-16 sm:py-24">
        <Reveal><SectionHead kicker="Our Partners" title="Sponsors" cta="Partner with us" to="/sponsors" /></Reveal>
        <Stagger className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6" data-testid="sponsors-wall" gap={0.06}>
          {sponsors.map((s) => (
            <StaggerItem key={s.id}>
              <div className="card-lux flex h-full flex-col items-center justify-center gap-2 p-6 text-center">
                <Award className="h-6 w-6 text-gold/70" />
                <p className="text-sm font-display text-amber-50">{s.name}</p>
                <p className="text-[0.6rem] uppercase tracking-wider text-muted-foreground">{s.tier}</p>
              </div>
            </StaggerItem>
          ))}
        </Stagger>
      </section>

      {/* INSTALL APP */}
      <section className="section pb-24">
        <div className="glass flex flex-col items-center gap-5 rounded-2xl p-10 text-center sm:flex-row sm:justify-between sm:text-left">
          <div>
            <p className="kicker mb-2">Africa-First · Works Offline</p>
            <h3 className="text-2xl sm:text-3xl text-amber-50">Add to your home screen</h3>
            <p className="mt-2 max-w-lg text-amber-100/60">Lightning-fast, low-data, offline-ready. Follow candidates and vote from anywhere on the continent.</p>
          </div>
          <button onClick={installApp} className="btn-gold inline-flex items-center gap-2" data-testid="install-app-btn">
            <Download className="h-4 w-4" /> Install App
          </button>
        </div>
      </section>
    </div>
  );
}
